(() => {
  const SELECTOR = "[data-dtno1-product]";
  const STORY_SELECTOR = "[data-dtno1-story]";

  class Dtno1EditorialProduct {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector(".dtno1-editorial-product__form");
      this.variantInput = root.querySelector("[data-variant-id]");
      this.variantData = this.readVariantData();
      this.currentVariant = this.variantData.find(
        (variant) => String(variant.id) === String(this.variantInput?.value),
      );
      this.purchasePanel = root.querySelector(".dtno1-editorial-product__purchase");
      this.mobileBuybar = root.querySelector("[data-mobile-buybar]");
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.bindGallery();
      this.bindOptions();
      this.bindQuantity();
      this.bindProductForm();
      this.observePurchasePanel();
      this.updateOptionAvailability();
    }

    destroy() {
      this.abortController.abort();
      this.purchaseObserver?.disconnect();
      this.root.removeAttribute("data-initialized");
    }

    readVariantData() {
      const script = this.root.querySelector("[data-variant-json]");
      if (!script) return [];

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error("DTNO.1 product variant data could not be parsed.", error);
        return [];
      }
    }

    bindGallery() {
      this.root.querySelectorAll("[data-media-target]").forEach((button) => {
        button.addEventListener(
          "click",
          () => this.activateMedia(button.dataset.mediaTarget, button.dataset.mediaPosition),
          { signal: this.signal },
        );
      });
    }

    activateMedia(mediaId, position) {
      if (!mediaId) return;

      this.root.querySelectorAll("[data-media-id]").forEach((media) => {
        const active = String(media.dataset.mediaId) === String(mediaId);
        media.hidden = !active;
        media.classList.toggle("is-active", active);

        if (!active) {
          media.querySelectorAll("video").forEach((video) => video.pause());
        }
      });

      this.root.querySelectorAll("[data-media-target]").forEach((button) => {
        const active = String(button.dataset.mediaTarget) === String(mediaId);
        button.classList.toggle("is-active", active);
        if (active) button.setAttribute("aria-current", "true");
        else button.removeAttribute("aria-current");
      });

      const counter = this.root.querySelector("[data-gallery-current]");
      if (counter && position) counter.textContent = String(position).padStart(2, "0");
    }

    bindOptions() {
      this.root.querySelectorAll("[data-option-input]").forEach((input) => {
        input.addEventListener(
          "change",
          () => {
            const selectedValues = this.selectedOptionValues();
            const variant = this.variantData.find((candidate) =>
              candidate.options.every((value, index) => value === selectedValues[index]),
            );

            this.updateOptionLegends();
            this.updateOptionAvailability();
            this.setVariant(variant || null);
          },
          { signal: this.signal },
        );
      });
    }

    selectedOptionValues() {
      const fieldsets = [...this.root.querySelectorAll("[data-option-position]")].sort(
        (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition),
      );

      return fieldsets.map((fieldset) => fieldset.querySelector("[data-option-input]:checked")?.value || "");
    }

    updateOptionLegends() {
      this.root.querySelectorAll("[data-option-position]").forEach((fieldset) => {
        const selected = fieldset.querySelector("[data-option-input]:checked");
        const choice = fieldset.querySelector("[data-option-choice]");
        if (choice) choice.textContent = selected?.value || "";

        fieldset.querySelectorAll("[data-option-input]").forEach((input) => {
          const state = input.nextElementSibling?.querySelector("[data-option-state]");
          if (state) state.textContent = input.checked ? "已选择" : input.disabled ? "不可用" : "选择";
        });
      });
    }

    updateOptionAvailability() {
      const selected = this.selectedOptionValues();

      this.root.querySelectorAll("[data-option-input]").forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);
        const hasAvailableVariant = this.variantData.some((variant) => {
          if (!variant.available || variant.options[optionIndex] !== input.value) return false;
          return variant.options.every((value, index) => index === optionIndex || value === selected[index]);
        });

        input.disabled = !hasAvailableVariant && !input.checked;
      });

      this.updateOptionLegends();
    }

    setVariant(variant) {
      this.currentVariant = variant;
      const addButton = this.root.querySelector("[data-add-button]");
      const mobileAddButton = this.root.querySelector("[data-mobile-add]");
      const addLabel = this.root.querySelector("[data-add-label]");
      const availability = this.root.querySelector("[data-availability]");

      if (!variant) {
        if (this.variantInput) this.variantInput.value = "";
        [addButton, mobileAddButton].forEach((button) => {
          if (button) button.disabled = true;
        });
        if (addLabel) addLabel.textContent = "该组合不可用";
        if (mobileAddButton) mobileAddButton.textContent = "该组合不可用";
        if (availability) availability.textContent = "请选择其他组合";
        this.updateUrl(null);
        return;
      }

      if (this.variantInput) this.variantInput.value = variant.id;
      this.renderVariant(variant);
      this.updateUrl(variant.id);

      if (variant.featured_media_id) {
        const thumb = this.root.querySelector(`[data-media-target="${variant.featured_media_id}"]`);
        this.activateMedia(variant.featured_media_id, thumb?.dataset.mediaPosition);
      }
    }

    renderVariant(variant) {
      const onSale = variant.compare_at_price > variant.price;
      const salePercent = onSale
        ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
        : 0;

      this.setText("[data-product-price]", variant.price_formatted);
      this.setText("[data-add-price]", variant.price_formatted);
      this.setText("[data-mobile-price]", variant.price_formatted);
      this.setText("[data-mobile-title]", variant.title);

      const comparePrice = this.root.querySelector("[data-compare-price]");
      if (comparePrice) {
        comparePrice.hidden = !onSale;
        comparePrice.textContent = onSale ? variant.compare_at_price_formatted : "";
      }

      const savings = this.root.querySelector("[data-savings]");
      if (savings) {
        savings.hidden = !onSale;
        savings.textContent = onSale ? `优惠 ${salePercent}%` : "";
      }

      const saleBadge = this.root.querySelector("[data-sale-badge]");
      if (saleBadge) saleBadge.hidden = !onSale;
      this.setText("[data-sale-percent]", onSale ? `−${salePercent}%` : "");

      const addButton = this.root.querySelector("[data-add-button]");
      const mobileAddButton = this.root.querySelector("[data-mobile-add]");
      [addButton, mobileAddButton].forEach((button) => {
        if (button) button.disabled = !variant.available;
      });

      this.setText("[data-add-label]", variant.available ? "加入购物车" : "暂时售罄");
      if (mobileAddButton) mobileAddButton.textContent = variant.available ? "加入购物车" : "暂时售罄";

      const availability = this.root.querySelector("[data-availability]");
      if (availability) {
        availability.textContent = variant.available
          ? "现货可选 · 结账时计算最终配送信息"
          : "当前款式暂时售罄";
      }
    }

    setText(selector, value) {
      const element = this.root.querySelector(selector);
      if (element) element.textContent = value || "";
    }

    updateUrl(variantId) {
      if (!window.history?.replaceState) return;
      const url = new URL(window.location.href);
      if (variantId) url.searchParams.set("variant", variantId);
      else url.searchParams.delete("variant");
      window.history.replaceState({}, "", url.toString());
    }

    bindQuantity() {
      const input = this.root.querySelector("[data-quantity-input]");
      if (!input) return;

      const normalize = (value) => Math.min(99, Math.max(1, Number.parseInt(value, 10) || 1));
      this.root.querySelector("[data-quantity-minus]")?.addEventListener(
        "click",
        () => {
          input.value = normalize(Number(input.value) - 1);
          input.dispatchEvent(new Event("change", { bubbles: true }));
        },
        { signal: this.signal },
      );
      this.root.querySelector("[data-quantity-plus]")?.addEventListener(
        "click",
        () => {
          input.value = normalize(Number(input.value) + 1);
          input.dispatchEvent(new Event("change", { bubbles: true }));
        },
        { signal: this.signal },
      );
      input.addEventListener("change", () => (input.value = normalize(input.value)), { signal: this.signal });
    }

    bindProductForm() {
      if (!this.form) return;
      if (this.root.dataset.ajaxCart !== "true") return;

      this.form.addEventListener(
        "submit",
        async (event) => {
          if (!this.currentVariant?.available) {
            event.preventDefault();
            return;
          }

          event.preventDefault();
          const addButton = this.root.querySelector("[data-add-button]");
          const addLabel = this.root.querySelector("[data-add-label]");
          const originalLabel = addLabel?.textContent || "加入购物车";

          if (addButton) {
            addButton.disabled = true;
            addButton.setAttribute("aria-busy", "true");
          }
          if (addLabel) addLabel.textContent = "正在加入…";

          try {
            const response = await fetch(this.root.dataset.cartAddUrl, {
              method: "POST",
              headers: { Accept: "application/json" },
              body: new FormData(this.form),
            });

            if (!response.ok) {
              const payload = await response.json().catch(() => ({}));
              throw new Error(payload.description || payload.message || "商品暂时无法加入购物车");
            }

            const item = await response.json();
            const cart = await this.fetchCart();
            this.showCartMessage(item, cart);
            document.dispatchEvent(new CustomEvent("dtno1:cart-added", { detail: { item, cart } }));
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
          } catch (error) {
            this.showCartError(error.message);
          } finally {
            if (addButton) {
              addButton.disabled = !this.currentVariant?.available;
              addButton.removeAttribute("aria-busy");
            }
            if (addLabel) addLabel.textContent = originalLabel;
          }
        },
        { signal: this.signal },
      );
    }

    async fetchCart() {
      const rootPath = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${rootPath}cart.js`, { headers: { Accept: "application/json" } });
      return response.ok ? response.json() : null;
    }

    showCartMessage(item, cart) {
      const message = this.root.querySelector("[data-cart-message]");
      if (!message) return;
      const count = cart?.item_count;
      const copy = message.querySelector("[data-cart-message-copy]");
      if (copy) copy.textContent = count ? `${item.product_title} · 购物车共 ${count} 件` : item.product_title;
      message.hidden = false;
      message.focus({ preventScroll: true });
    }

    showCartError(errorMessage) {
      const message = this.root.querySelector("[data-cart-message]");
      if (!message) return;
      const heading = message.querySelector("strong");
      const copy = message.querySelector("[data-cart-message-copy]");
      if (heading) heading.textContent = "加入失败";
      if (copy) copy.textContent = `${errorMessage}，请重试。`;
      message.hidden = false;
      message.focus({ preventScroll: true });
    }

    observePurchasePanel() {
      if (!this.purchasePanel || !this.mobileBuybar || !("IntersectionObserver" in window)) return;

      this.purchaseObserver = new IntersectionObserver(([entry]) => {
        const visible = entry.boundingClientRect.bottom < 0;
        this.mobileBuybar.classList.toggle("is-visible", visible);
        this.mobileBuybar.setAttribute("aria-hidden", String(!visible));
      });
      this.purchaseObserver.observe(this.purchasePanel);
    }
  }

  function initialize(scope = document) {
    scope.querySelectorAll(`${SELECTOR}:not([data-initialized])`).forEach((root) => {
      root.setAttribute("data-initialized", "true");
      root.dtno1EditorialProduct = new Dtno1EditorialProduct(root);
    });
    syncEditorialPageClass();
  }

  function syncEditorialPageClass() {
    document.documentElement.classList.toggle(
      "dtno1-editorial-page",
      Boolean(document.querySelector(STORY_SELECTOR)),
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelector(SELECTOR)?.dtno1EditorialProduct?.destroy();
    window.requestAnimationFrame(syncEditorialPageClass);
  });
})();
